import React from 'react';
import {addWeeks, format, startOfWeek} from 'date-fns';
import Image from 'next/image';
import {useRouter} from 'next/router';

import {CheckInStat} from '#apollo/schema';
import {Button, Icon, RadialProgress, Text} from '#component/base';
import {ActionPlanPage} from '#component/page/ActionPlan/ActionPlan.types';
import {sprinkles} from '#style/sprinkles';

import {CheckInInfos} from './CheckInSection.types';

import * as css from './CheckInSection.css';

const useCheckInInfos = (
  canCheckIn: boolean,
  pageState: ActionPlanPage.State,
  dateNextCheckIn?: string | null,
): CheckInInfos => {
  const {push} = useRouter();

  switch (pageState) {
    case 'inactive':
      return {
        title: `Unlock Check-ins`,
        description: (
          <>
            <Text as="p">
              Your health goals are within reach! Weekly check-ins provide you with the
              accountability and support you need to start living healthier, longer.
            </Text>
            <Text
              as="p"
              fontWeight="semiBold"
              className={sprinkles({
                mT: 18,
              })}>
              Your check-ins are unavailable until you opt-in to an Action Plan.
            </Text>
          </>
        ),
        cta: {
          label: 'Activate Plan',
          variant: 'primary',
          action: () => push('actionplan/edit'),
        },
        icon: 'Lock',
      };
    case 'error':
      return {
        title: `Unlock Check-ins`,
        description: (
          <>
            <Text as="p">
              Your health goals are within reach! Weekly check-ins provide you with the
              accountability and support you need to start living healthier, longer.
            </Text>
            <Text
              as="p"
              fontWeight="semiBold"
              className={sprinkles({
                mT: 18,
              })}>
              Your check-ins are unavailable until you opt-in to an Action Plan.
            </Text>
          </>
        ),
        cta: {
          label: 'Contact Support',
          variant: 'primary',
          action: () => push('/support'),
        },
        icon: 'Lock',
      };
    case 'missing-lab':
      return {
        title: 'Check-ins Locked',
        description: (
          <>
            <Text as="p">
              We’re here to help you stay accountable with your Action Plan through periodic
              check-ins designed to support you getting across the finish line.
            </Text>
            <Text
              as="p"
              fontWeight="semiBold"
              className={sprinkles({
                mT: 18,
                mB: [24, 0],
              })}>
              Your check-ins will become available once your Action Plan is activated.
            </Text>
          </>
        ),
        icon: 'Lock',
      };
    case 'missing-lab-result':
      return {
        title: 'Check-ins Locked',
        description: (
          <>
            <Text as="p">
              Your health goals are within reach! Weekly check-ins provide you with the
              accountability and support to start living healthier, longer.
            </Text>
            <Text
              as="p"
              fontWeight="semiBold"
              className={sprinkles({
                mT: 18,
                mB: [24, 0],
              })}>
              Your check-ins are unavailable until you opt-in to an Action Plan.
            </Text>
          </>
        ),
        icon: 'Lock',
      };
    case 'missing-survey':
    case 'missing-survey-and-lab':
      return {
        title: 'Unlock Check-ins',
        description: (
          <>
            <Text as="p">
              {
                "We're here to help you stay accountable with your Action Plan through periodic check-ins designed to support you getting across the finish line."
              }
            </Text>
            <Text
              as="p"
              fontWeight="semiBold"
              className={sprinkles({
                mT: 18,
              })}>
              Your check-ins will become available once your Action Plan is activated.
            </Text>
          </>
        ),
        icon: 'Lock',
        cta: {
          label: 'Take your Lifestyle Survey',
          variant: 'primary',
          action: () => push('/survey'),
        },
      };
    case 'active': {
      return {
        title: 'Check-ins Tracker',
        description: (
          <>
            <Text as="p">
              Notifications are sent out on a weekly basis. Of your total possible check-ins for
              this Action Plan cycle, you’ve checked in 0% of the time.
            </Text>
            <Text
              as="p"
              fontWeight="semiBold"
              className={sprinkles({
                mT: 18,
              })}>
              Your next check-in is: <Text fontWeight="semiBold">{dateNextCheckIn}</Text>
            </Text>
          </>
        ),
        icon: 'CircleWavyCheck',
        cta: {
          label: canCheckIn ? 'Check-in' : 'View Latest Summary',
          variant: 'primary',
          action: () => push('/checkin'),
        },
      };
    }
    default:
      return {
        title: `Unlock Check-ins`,
        description: (
          <>
            <Text as="p">
              We’re here to help you stay accountable with your Action Plan through periodic
              check-ins designed to support you getting across the finish line.
            </Text>
            <Text
              as="p"
              fontWeight="semiBold"
              className={sprinkles({
                mT: 18,
              })}>
              Available to members only.
            </Text>
          </>
        ),
        cta: {
          label: 'Become a member',
          variant: 'primary',
          action: () => push('/shop/products/membership'),
        },
        icon: 'Lock',
      };
  }
};

export interface CheckInSectionProps {
  pageState: ActionPlanPage.State;
  checkInStats: CheckInStat[];
  currentCheckIn?: ActionPlanPage.CheckIn | null;
}
function CheckInSection({pageState, checkInStats, currentCheckIn}: CheckInSectionProps) {
  const checkInInfos = useCheckInInfos(
    !!currentCheckIn && !currentCheckIn?.completed,
    pageState,
    format(startOfWeek(addWeeks(new Date(), 1), {weekStartsOn: 1}), 'EEEE, MMM d'),
  );
  const completedCheckInsCount = checkInStats?.filter(({completed}) => !!completed).length || 0;
  const totalCheckInsCount = checkInStats?.length || 0;
  return (
    <div
      className={sprinkles({
        position: 'relative',
        layout: 'flex',
        dir: ['col', 'row'],
      })}>
      <div
        className={sprinkles({
          position: 'relative',
          display: 'flex',
          dir: 'col',
        })}>
        <div
          className={sprinkles({
            display: 'flex',
            items: ['start', 'center'],
            mB: 20,
            dir: ['col', 'row'],
          })}>
          {checkInInfos.icon && (
            <Icon
              aria-hidden
              weight="thin"
              color="burgundy"
              name={checkInInfos.icon}
              className={sprinkles({
                mR: 10,
                size: [48, 64],
                mB: [20, 0],
              })}
            />
          )}
          <Text as="h2" variant="heading2" color="deepBurgundy">
            {checkInInfos.title}
          </Text>
        </div>
        <Text as="div" color="doveGray" variant="bodyLarge" className={css.description}>
          {checkInInfos.description}
        </Text>
        <div
          className={sprinkles({
            display: 'flex',
            gap: [0, 40],
            mT: [0, 40],
            items: 'center',
          })}>
          {checkInInfos.cta && (
            <div
              className={sprinkles({
                display: 'flex',
                content: 'between',
                mY: [24, 0],
                width: ['full', 'auto'],
              })}>
              <Button
                className={sprinkles({
                  width: 'full',
                })}
                variant={checkInInfos.cta.variant}
                onClick={checkInInfos.cta.action}>
                {checkInInfos.cta.label}
              </Button>
            </div>
          )}
        </div>
      </div>
      <div>
        <div className={css.progressWrapper}>
          {pageState === 'active' ? (
            <RadialProgress
              className={css.progress}
              label={
                <Text color="inherit" variant="heading2">
                  {completedCheckInsCount}
                  <Text color="inherit" variant="bodyLarge">
                    {`/${totalCheckInsCount}`}
                  </Text>
                </Text>
              }
              value={completedCheckInsCount}
              maxValue={totalCheckInsCount}
            />
          ) : (
            <Image
              className={css.progress}
              src="/image/checkin-lock.svg"
              alt="no-score"
              height={300}
              width={300}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export {CheckInSection};
